import type { OpportunityStatus, ProductType } from '@fig/shared';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ADVISOR_STATUS_FILTER_OPTIONS, formatAdvisorStatus } from '@/lib/advisor-status';
import { ALL_FILTER_VALUE } from '@/lib/filter-constants';
import { INCOME_STATUS_FILTER_OPTIONS } from '@/lib/income';
import {
  DEFAULT_OPPORTUNITY_STATUS,
  formatOpportunityStatus,
  OPPORTUNITY_STATUS_OPTIONS,
} from '@/lib/opportunity-status';
import { FIG_PRODUCT_TYPE_OPTIONS } from '@/lib/product-types';
import { cn } from '@/lib/utils';

interface FilterValues {
  salesTeamId?: string;
  advisorId?: string;
  carrierName?: string;
  productType?: ProductType;
  outOfSurrender?: boolean;
  minScore?: number;
  maxScore?: number;
  minClientAge?: number;
  maxClientAge?: number;
  minSurrenderLoss?: number;
  maxSurrenderLoss?: number;
  taxQualification?: string;
  status?: OpportunityStatus;
  clientName?: string;
  incomeStatus?: string;
  advisorStatus?: string;
}

const TAX_QUALIFICATION_OPTIONS = ['Qualified', 'Non-Qualified'];

const INCOME_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  INCOME_STATUS_FILTER_OPTIONS.map((opt) => [opt.value, opt.label])
);

export interface SalesTeamOption {
  value: string;
  label: string;
}

interface FilterPanelProps {
  filters: FilterValues;
  onFilterChange: (key: keyof FilterValues, value: string | number | boolean | undefined) => void;
  onClearFilters: () => void;
  variant?: 'default' | 'overlay' | 'surface';
  /**
   * Sales teams the current user is assigned to. The sales-team filter is only
   * rendered when the user belongs to more than one team; a single team (or
   * none) needs no narrowing control.
   */
  salesTeamOptions?: SalesTeamOption[];
}

const PRODUCT_TYPE_OPTIONS = FIG_PRODUCT_TYPE_OPTIONS.map((productType) => ({
  label: productType,
  value: productType,
}));

function clampScore(value: string): number | undefined {
  if (!value) return undefined;
  const num = Number(value);
  if (isNaN(num)) return undefined;
  return Math.max(0, Math.min(100, Math.round(num)));
}

function clampAge(value: string): number | undefined {
  if (!value) return undefined;
  const num = Number(value);
  if (isNaN(num)) return undefined;
  return Math.max(0, Math.min(120, Math.round(num)));
}

function clampPercent(value: string): number | undefined {
  if (!value) return undefined;
  const num = Number(value);
  if (isNaN(num)) return undefined;
  return Math.max(0, Math.min(100, Math.round(num * 10) / 10));
}

function useDebouncedInput(externalValue: string, onCommit: (value: string) => void, delay = 500) {
  const [local, setLocal] = useState(externalValue);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocal(externalValue);
  }, [externalValue]);

  const onChange = useCallback(
    (value: string) => {
      setLocal(value);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onCommit(value), delay);
    },
    [onCommit, delay]
  );

  const onBlur = useCallback(() => {
    clearTimeout(timerRef.current);
    onCommit(local);
  }, [onCommit, local]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        clearTimeout(timerRef.current);
        onCommit(local);
      }
    },
    [onCommit, local]
  );

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return { value: local, onChange, onBlur, onKeyDown };
}

type FilterPanelVariant = NonNullable<FilterPanelProps['variant']>;

function Divider({ variant = 'default' }: { variant?: FilterPanelVariant }) {
  return (
    <div
      className={cn('h-5 w-px shrink-0', variant === 'overlay' ? 'bg-white/10' : 'bg-border/50')}
    />
  );
}

type SurrenderValue = 'any' | 'yes' | 'no';

function SurrenderToggle({
  value,
  onChange,
  variant = 'default',
}: {
  value: SurrenderValue;
  onChange: (v: SurrenderValue) => void;
  variant?: FilterPanelVariant;
}) {
  const options: { label: string; val: SurrenderValue }[] = [
    { label: 'Any', val: 'any' },
    { label: 'Yes', val: 'yes' },
    { label: 'No', val: 'no' },
  ];

  return (
    <div className="flex items-center">
      <span
        className={cn(
          'mr-1.5 whitespace-nowrap text-xs',
          variant === 'overlay' ? 'text-slate-400' : 'text-muted-foreground'
        )}
      >
        Surrender
      </span>
      <div
        className="inline-flex h-7 items-center rounded-md p-0.5"
        style={{
          backgroundColor: variant === 'overlay' ? 'rgba(255,255,255,0.08)' : 'hsl(var(--muted))',
        }}
      >
        {options.map((opt) => (
          <button
            key={opt.val}
            onClick={() => onChange(opt.val)}
            className={cn(
              'h-6 rounded px-2 text-xs font-medium transition-colors',
              value === opt.val && variant === 'overlay'
                ? 'bg-white/20 text-white shadow-sm'
                : value === opt.val
                  ? 'bg-background text-foreground shadow-sm'
                  : variant === 'overlay'
                    ? 'text-slate-400 hover:text-white'
                    : 'text-muted-foreground hover:text-foreground'
            )}
            style={
              value === opt.val && variant !== 'overlay'
                ? { backgroundColor: 'hsl(var(--background))' }
                : undefined
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FilterPanel({
  filters,
  onFilterChange,
  onClearFilters,
  variant = 'default',
  salesTeamOptions = [],
}: FilterPanelProps) {
  const overlay = variant === 'overlay';
  const surface = variant === 'surface';
  // Only offer the sales-team narrowing filter to users tied to more than one team.
  const showSalesTeamFilter = salesTeamOptions.length > 1;
  const labelClassName = overlay ? 'text-slate-400' : 'text-muted-foreground';
  const inputClassName = overlay
    ? 'border-white/10 bg-white/10 text-white placeholder:text-slate-500 shadow-none focus-visible:ring-1 focus-visible:ring-white/25'
    : 'border-input bg-background shadow-none focus-visible:ring-1';
  const selectClassName = overlay
    ? 'border-white/10 bg-white/10 text-white data-[placeholder]:text-slate-500'
    : '';

  const activeChips: { key: string; label: string; value: string }[] = [];
  if (filters.status && filters.status !== DEFAULT_OPPORTUNITY_STATUS) {
    activeChips.push({
      key: 'status',
      label: 'Status',
      value: formatOpportunityStatus(filters.status),
    });
  }
  if (filters.carrierName) {
    activeChips.push({ key: 'carrierName', label: 'Search', value: filters.carrierName });
  }
  if (filters.productType)
    activeChips.push({ key: 'productType', label: 'Product', value: filters.productType });
  if (filters.minScore !== undefined || filters.maxScore !== undefined) {
    activeChips.push({
      key: 'score',
      label: 'Score',
      value: `${filters.minScore ?? 0}–${filters.maxScore ?? 100}`,
    });
  }
  if (filters.minClientAge !== undefined || filters.maxClientAge !== undefined) {
    activeChips.push({
      key: 'clientAge',
      label: 'Age',
      value: `${filters.minClientAge ?? 0}–${filters.maxClientAge ?? 120}`,
    });
  }
  if (filters.minSurrenderLoss !== undefined || filters.maxSurrenderLoss !== undefined) {
    activeChips.push({
      key: 'surrenderLoss',
      label: 'Surr. Loss',
      value: `${filters.minSurrenderLoss ?? 0}–${filters.maxSurrenderLoss ?? 100}%`,
    });
  }
  if (filters.taxQualification) {
    activeChips.push({
      key: 'taxQualification',
      label: 'Tax',
      value: filters.taxQualification,
    });
  }
  if (filters.clientName) {
    activeChips.push({ key: 'clientName', label: 'Client', value: filters.clientName });
  }
  if (filters.incomeStatus) {
    activeChips.push({
      key: 'incomeStatus',
      label: 'Income',
      value: INCOME_STATUS_LABELS[filters.incomeStatus] ?? filters.incomeStatus,
    });
  }
  if (filters.advisorStatus) {
    activeChips.push({
      key: 'advisorStatus',
      label: 'Advisor Status',
      value: formatAdvisorStatus(filters.advisorStatus),
    });
  }
  if (filters.outOfSurrender !== undefined) {
    activeChips.push({
      key: 'outOfSurrender',
      label: 'Surrender',
      value: filters.outOfSurrender ? 'Yes' : 'No',
    });
  }
  if (showSalesTeamFilter && filters.salesTeamId) {
    const selectedTeam = salesTeamOptions.find((opt) => opt.value === filters.salesTeamId);
    activeChips.push({
      key: 'salesTeamId',
      label: 'Sales Team',
      value: selectedTeam?.label ?? filters.salesTeamId,
    });
  }
  if (filters.advisorId) {
    activeChips.push({ key: 'advisorId', label: 'Advisor', value: filters.advisorId });
  }

  const handleRemoveChip = (key: string) => {
    if (key === 'score') {
      onFilterChange('minScore', undefined);
      onFilterChange('maxScore', undefined);
    } else if (key === 'clientAge') {
      onFilterChange('minClientAge', undefined);
      onFilterChange('maxClientAge', undefined);
    } else if (key === 'surrenderLoss') {
      onFilterChange('minSurrenderLoss', undefined);
      onFilterChange('maxSurrenderLoss', undefined);
    } else {
      onFilterChange(key as keyof FilterValues, undefined);
    }
  };

  const search = useDebouncedInput(
    filters.carrierName ?? '',
    useCallback((val: string) => onFilterChange('carrierName', val || undefined), [onFilterChange])
  );

  const minScore = useDebouncedInput(
    filters.minScore?.toString() ?? '',
    useCallback((val: string) => onFilterChange('minScore', clampScore(val)), [onFilterChange])
  );

  const maxScore = useDebouncedInput(
    filters.maxScore?.toString() ?? '',
    useCallback((val: string) => onFilterChange('maxScore', clampScore(val)), [onFilterChange])
  );

  const minClientAge = useDebouncedInput(
    filters.minClientAge?.toString() ?? '',
    useCallback((val: string) => onFilterChange('minClientAge', clampAge(val)), [onFilterChange])
  );

  const maxClientAge = useDebouncedInput(
    filters.maxClientAge?.toString() ?? '',
    useCallback((val: string) => onFilterChange('maxClientAge', clampAge(val)), [onFilterChange])
  );

  const minSurrenderLoss = useDebouncedInput(
    filters.minSurrenderLoss?.toString() ?? '',
    useCallback(
      (val: string) => onFilterChange('minSurrenderLoss', clampPercent(val)),
      [onFilterChange]
    )
  );

  const maxSurrenderLoss = useDebouncedInput(
    filters.maxSurrenderLoss?.toString() ?? '',
    useCallback(
      (val: string) => onFilterChange('maxSurrenderLoss', clampPercent(val)),
      [onFilterChange]
    )
  );

  const advisor = useDebouncedInput(
    filters.advisorId ?? '',
    useCallback((val: string) => onFilterChange('advisorId', val || undefined), [onFilterChange])
  );

  const clientName = useDebouncedInput(
    filters.clientName ?? '',
    useCallback((val: string) => onFilterChange('clientName', val || undefined), [onFilterChange])
  );

  const surrenderValue: SurrenderValue =
    filters.outOfSurrender === true ? 'yes' : filters.outOfSurrender === false ? 'no' : 'any';

  const handleSurrenderChange = (v: SurrenderValue) => {
    onFilterChange('outOfSurrender', v === 'any' ? undefined : v === 'yes');
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollWidth - el.scrollLeft - el.clientWidth > 1);
  }, []);

  const handleScrollLeft = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  const handleScrollRight = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  return (
    <div className="min-w-0 space-y-2">
      {/* Controls container with border */}
      <div
        className={cn(
          'min-w-0 rounded-lg border',
          overlay && 'border-white/10 bg-black/55 text-white shadow-elevation-2 backdrop-blur-xl',
          surface && 'bg-gradient-card backdrop-blur-xl'
        )}
        style={surface ? { boxShadow: 'var(--shadow-1), var(--inset-highlight)' } : undefined}
      >
        {/* Labels row inside container */}
        <div className="flex items-stretch">
          <div className="flex w-48 shrink-0 items-center px-3 pt-2 pb-0">
            <span className={cn('text-xs font-medium', labelClassName)}>Search</span>
          </div>
          <div
            className={cn('w-px shrink-0', overlay ? 'bg-white/10' : 'bg-border')}
            style={overlay ? undefined : { backgroundColor: 'hsl(var(--border))' }}
          />
          <div className="min-w-0 flex-1 px-3 pt-2 pb-0">
            <div className="flex items-center gap-2">
              <span className={cn('text-xs font-medium', labelClassName)}>Filters</span>
              {activeChips.length > 0 && (
                <Badge
                  variant="secondary"
                  className={cn(
                    'h-4 min-w-4 px-1 text-[10px]',
                    overlay && 'bg-white/10 text-white'
                  )}
                >
                  {activeChips.length}
                </Badge>
              )}
              <div className="flex-1" />
              <div className="flex items-center gap-1">
                {canScrollLeft && (
                  <button
                    type="button"
                    onClick={handleScrollLeft}
                    aria-label="Scroll filters left"
                    className={cn(
                      'rounded-sm transition-colors',
                      overlay
                        ? 'text-slate-400 hover:text-white'
                        : 'text-muted-foreground/60 hover:text-foreground'
                    )}
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </button>
                )}
                {canScrollRight && (
                  <button
                    type="button"
                    onClick={handleScrollRight}
                    aria-label="Scroll filters right"
                    className={cn(
                      'rounded-sm transition-colors',
                      overlay
                        ? 'text-slate-400 hover:text-white'
                        : 'text-muted-foreground/60 hover:text-foreground'
                    )}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex min-w-0 items-stretch">
          {/* Search — fixed width */}
          <div className="flex w-48 shrink-0 items-center px-3 pb-2 pt-1">
            <div className="relative w-full">
              <Search
                className={cn(
                  'absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
                  labelClassName
                )}
              />
              <Input
                placeholder="Carrier or product..."
                className={cn('h-7 border pl-8 text-xs', inputClassName)}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                onBlur={search.onBlur}
                onKeyDown={search.onKeyDown}
              />
            </div>
          </div>

          {/* Solid divider — same color as container border */}
          <div
            className={cn('w-px shrink-0', overlay ? 'bg-white/10' : 'bg-border')}
            style={overlay ? undefined : { backgroundColor: 'hsl(var(--border))' }}
          />

          {/* Scrollable filters area */}
          <div className="relative min-w-0 flex-1 overflow-hidden pb-2 pl-3 pt-1">
            <div
              ref={scrollRef}
              className="flex items-center gap-2 overflow-x-auto pr-10 scrollbar-hide"
            >
              {/* Group 1: Status, Advisor & Product */}
              <Select
                value={filters.status ?? ALL_FILTER_VALUE}
                onValueChange={(value) => onFilterChange('status', value)}
              >
                <SelectTrigger className={cn('h-7 w-36 shrink-0 text-xs', selectClassName)}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>All Statuses</SelectItem>
                  {OPPORTUNITY_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sales team filter — only shown to users tied to more than one
                  sales team, positioned immediately left of the Advisor filter.
                  Narrows within the user's already-scoped teams. */}
              {showSalesTeamFilter && (
                <Select
                  value={filters.salesTeamId ?? ALL_FILTER_VALUE}
                  onValueChange={(value) =>
                    onFilterChange('salesTeamId', value === ALL_FILTER_VALUE ? undefined : value)
                  }
                >
                  <SelectTrigger className={cn('h-7 w-44 shrink-0 text-xs', selectClassName)}>
                    <SelectValue placeholder="Sales Team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>All Sales Teams</SelectItem>
                    {salesTeamOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Advisor filter (free-text name filter, available to all roles —
                  VPs are already team-scoped server-side, so narrowing further is safe) */}
              <Input
                placeholder="Advisor name..."
                className={cn('h-7 w-40 shrink-0 text-xs', inputClassName)}
                value={advisor.value}
                onChange={(e) => advisor.onChange(e.target.value)}
                onBlur={advisor.onBlur}
                onKeyDown={advisor.onKeyDown}
              />

              {/* Advisor status sits beside the advisor it describes. Sticky
                  across sessions -- see useOpportunityFilters (T20-3323). */}
              <Select
                value={filters.advisorStatus ?? ALL_FILTER_VALUE}
                onValueChange={(value) =>
                  onFilterChange('advisorStatus', value === ALL_FILTER_VALUE ? undefined : value)
                }
              >
                <SelectTrigger className={cn('h-7 w-44 shrink-0 text-xs', selectClassName)}>
                  <SelectValue placeholder="Advisor Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>All Advisor Statuses</SelectItem>
                  {ADVISOR_STATUS_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Client name..."
                className={cn('h-7 w-40 shrink-0 text-xs', inputClassName)}
                value={clientName.value}
                onChange={(e) => clientName.onChange(e.target.value)}
                onBlur={clientName.onBlur}
                onKeyDown={clientName.onKeyDown}
              />

              <Select
                value={filters.productType ?? ALL_FILTER_VALUE}
                onValueChange={(value) =>
                  onFilterChange('productType', value === ALL_FILTER_VALUE ? undefined : value)
                }
              >
                <SelectTrigger className={cn('h-7 w-56 shrink-0 text-xs', selectClassName)}>
                  <SelectValue placeholder="Product Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>All Product Types</SelectItem>
                  {PRODUCT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.incomeStatus ?? ALL_FILTER_VALUE}
                onValueChange={(value) =>
                  onFilterChange('incomeStatus', value === ALL_FILTER_VALUE ? undefined : value)
                }
              >
                <SelectTrigger className={cn('h-7 w-44 shrink-0 text-xs', selectClassName)}>
                  <SelectValue placeholder="Income" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>All Income Statuses</SelectItem>
                  {INCOME_STATUS_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Divider variant={variant} />

              {/* Group 2: Score range */}
              <div className="flex shrink-0 items-center gap-1">
                <span className={cn('whitespace-nowrap text-xs', labelClassName)}>Score</span>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Min"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={minScore.value}
                  onChange={(e) => minScore.onChange(e.target.value)}
                  onBlur={minScore.onBlur}
                  onKeyDown={minScore.onKeyDown}
                />
                <span className={cn('text-xs', labelClassName)}>–</span>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Max"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={maxScore.value}
                  onChange={(e) => maxScore.onChange(e.target.value)}
                  onBlur={maxScore.onBlur}
                  onKeyDown={maxScore.onKeyDown}
                />
              </div>

              <Divider variant={variant} />

              {/* Group 3: Client age range */}
              <div className="flex shrink-0 items-center gap-1">
                <span className={cn('whitespace-nowrap text-xs', labelClassName)}>Age</span>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Min"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={minClientAge.value}
                  onChange={(e) => minClientAge.onChange(e.target.value)}
                  onBlur={minClientAge.onBlur}
                  onKeyDown={minClientAge.onKeyDown}
                />
                <span className={cn('text-xs', labelClassName)}>–</span>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Max"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={maxClientAge.value}
                  onChange={(e) => maxClientAge.onChange(e.target.value)}
                  onBlur={maxClientAge.onBlur}
                  onKeyDown={maxClientAge.onKeyDown}
                />
              </div>

              <Divider variant={variant} />

              {/* Group 4: Surrender loss % range */}
              <div className="flex shrink-0 items-center gap-1">
                <span className={cn('whitespace-nowrap text-xs', labelClassName)}>
                  Surr. Loss %
                </span>
                <Input
                  inputMode="decimal"
                  placeholder="Min"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={minSurrenderLoss.value}
                  onChange={(e) => minSurrenderLoss.onChange(e.target.value)}
                  onBlur={minSurrenderLoss.onBlur}
                  onKeyDown={minSurrenderLoss.onKeyDown}
                />
                <span className={cn('text-xs', labelClassName)}>–</span>
                <Input
                  inputMode="decimal"
                  placeholder="Max"
                  className={cn(
                    'h-7 w-14 text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]',
                    inputClassName
                  )}
                  value={maxSurrenderLoss.value}
                  onChange={(e) => maxSurrenderLoss.onChange(e.target.value)}
                  onBlur={maxSurrenderLoss.onBlur}
                  onKeyDown={maxSurrenderLoss.onKeyDown}
                />
              </div>

              <Divider variant={variant} />

              {/* Group 5: Tax qualification */}
              <Select
                value={filters.taxQualification ?? ALL_FILTER_VALUE}
                onValueChange={(value) =>
                  onFilterChange('taxQualification', value === ALL_FILTER_VALUE ? undefined : value)
                }
              >
                <SelectTrigger className={cn('h-7 w-40 shrink-0 text-xs', selectClassName)}>
                  <SelectValue placeholder="Tax Qualification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>All Tax Qualifications</SelectItem>
                  {TAX_QUALIFICATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Divider variant={variant} />

              {/* Group 6: Out of Surrender */}
              <SurrenderToggle
                value={surrenderValue}
                onChange={handleSurrenderChange}
                variant={variant}
              />

              {/* Clear button — always last */}
              {activeChips.length > 0 && (
                <>
                  <Divider variant={variant} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearFilters}
                    className={cn(
                      'h-7 shrink-0 text-xs',
                      overlay
                        ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                        : 'text-muted-foreground'
                    )}
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
            {/* Right fade gradient for scroll overflow */}
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-lg"
              style={{
                background: overlay
                  ? 'linear-gradient(to right, transparent, rgba(0,0,0,0.8))'
                  : 'linear-gradient(to right, transparent, hsl(var(--card)))',
              }}
            />
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeChips.map((chip) => (
            <Badge
              key={chip.key}
              variant="outline"
              className={cn(
                'gap-1 pr-1',
                overlay && 'border-white/15 bg-black/45 text-white backdrop-blur',
                surface && 'bg-card/95 shadow-elevation-1 backdrop-blur'
              )}
            >
              <span className={overlay ? 'text-slate-400' : 'text-muted-foreground'}>
                {chip.label}:
              </span>
              {chip.value}
              <button
                onClick={() => handleRemoveChip(chip.key)}
                className={cn(
                  'ml-0.5 rounded-sm',
                  overlay ? 'hover:bg-white/10' : 'hover:bg-muted'
                )}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
